import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { 
  Boxes, Search, Plus, Minus, Edit2, RefreshCw, 
  AlertTriangle, CheckCircle, XCircle, History
} from 'lucide-react';

interface InventoryItem {
  id: string;
  variantId: string;
  logicalStock: number;
  allocatedStock: number;
  freeStock: number;
  safetyStock: number;
  isSyncEnabled: boolean;
  variant: {
    id: string;
    variantCode: string;
    size?: string;
    color?: string;
    masterId: string;
  } | null;
  product: {
    id: string;
    skuCode: string;
    productName: string;
  } | null;
}

interface InventorySummary {
  totalProducts: number;
  lowStockCount: number;
  outOfStockCount: number;
  syncDisabledCount: number;
}

export default function InventoryPage() {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [summary, setSummary] = useState<InventorySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState<InventoryItem | null>(null);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    fetchInventory();
    fetchSummary();
  }, [showLowStock]);

  async function fetchInventory() {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '100',
        ...(showLowStock && { lowStock: 'true' }),
        ...(search && { search }),
      });

      const response = await apiFetch(`/api/inventory?${params}`);
      const data = await response.json();

      if (!response.ok) throw new Error(data.error);
      setInventory(Array.isArray(data.inventory) ? data.inventory : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSummary() {
    try {
      const response = await apiFetch('/api/inventory/summary');
      const data = await response.json();
      if (response.ok) setSummary(data.summary);
    } catch (err) {
      console.error('Summary fetch error:', err);
    }
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Boxes size={28} />
            在庫管理
          </h1>
          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
            全商品の在庫を一元管理・同期
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowLogs(true)} className="btn-secondary flex items-center gap-2">
            <History size={16} />
            変動履歴
          </button>
          <button onClick={fetchInventory} className="btn-secondary flex items-center gap-2">
            <RefreshCw size={16} />
            更新
          </button>
        </div>
      </div>

      {/* サマリーカード */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <Boxes size={20} className="text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{summary.totalProducts}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>総SKU数</p>
              </div>
            </div>
          </div>
          <div className="card p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowLowStock(!showLowStock)}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                <AlertTriangle size={20} className="text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{summary.lowStockCount}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>在庫不足</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                <XCircle size={20} className="text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{summary.outOfStockCount}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>在庫切れ</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-500 flex items-center justify-center">
                <RefreshCw size={20} className="text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{summary.syncDisabledCount}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>同期停止中</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* フィルタ・検索 */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <Search size={18} />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchInventory()}
            placeholder="商品名、SKUコードで検索..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
            style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showLowStock}
            onChange={(e) => setShowLowStock(e.target.checked)}
            className="rounded"
          />
          <span style={{ color: 'var(--text-primary)' }}>在庫不足のみ表示</span>
        </label>
        <button onClick={fetchInventory} className="btn-primary">反映</button>
      </div>

      {/* メッセージ */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <XCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">×</button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto">×</button>
        </div>
      )}

      {/* 在庫一覧 */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>商品</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>バリアント</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>総在庫</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>引当済</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>販売可</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>安全在庫</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>同期</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>操作</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {inventory.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                    在庫データがありません
                  </td>
                </tr>
              ) : (
                inventory.map((item) => {
                  const isLowStock = item.freeStock <= item.safetyStock && item.freeStock > 0;
                  const isOutOfStock = item.freeStock === 0;

                  return (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {item.product?.productName || '-'}
                          </p>
                          <p className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                            {item.product?.skuCode}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <span className="font-mono" style={{ color: 'var(--text-primary)' }}>
                            {item.variant?.variantCode}
                          </span>
                          {(item.variant?.size || item.variant?.color) && (
                            <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>
                              {[item.variant.size, item.variant.color].filter(Boolean).join(' / ')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>
                        {item.logicalStock}
                      </td>
                      <td className="px-4 py-3 text-right" style={{ color: 'var(--text-secondary)' }}>
                        {item.allocatedStock}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold ${isOutOfStock ? 'text-red-600' : isLowStock ? 'text-orange-600' : ''}`} style={!isOutOfStock && !isLowStock ? { color: 'var(--text-primary)' } : {}}>
                          {item.freeStock}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" style={{ color: 'var(--text-secondary)' }}>
                        {item.safetyStock}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.isSyncEnabled ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                            <CheckCircle size={12} /> 有効
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                            停止
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setAdjustingItem(item)}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          title="在庫調整"
                        >
                          <Edit2 size={16} style={{ color: 'var(--text-secondary)' }} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 在庫調整モーダル */}
      {adjustingItem && (
        <AdjustInventoryModal
          item={adjustingItem}
          onClose={() => setAdjustingItem(null)}
          onSuccess={() => {
            setAdjustingItem(null);
            setSuccess('在庫を調整しました');
            fetchInventory();
            fetchSummary();
          }}
        />
      )}

      {/* 変動履歴モーダル */}
      {showLogs && (
        <InventoryLogsModal onClose={() => setShowLogs(false)} />
      )}
    </div>
  );
}

// 在庫調整モーダル
function AdjustInventoryModal({ item, onClose, onSuccess }: { item: InventoryItem; onClose: () => void; onSuccess: () => void }) {
  const [adjustmentType, setAdjustmentType] = useState<'set' | 'add' | 'subtract'>('set');
  const [quantity, setQuantity] = useState(item.logicalStock);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reason.trim()) {
      setError('調整理由を入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiFetch('/api/inventory/adjust', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variantId: item.variantId,
          adjustmentType,
          quantity,
          reason,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md rounded-lg shadow-xl" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>在庫調整</h2>
          
          <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{item.product?.productName}</p>
            <p className="text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>{item.variant?.variantCode}</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>現在の総在庫: {item.logicalStock}</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>調整タイプ</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setAdjustmentType('set')} className={`flex-1 py-2 rounded-lg border transition-colors ${adjustmentType === 'set' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300'}`}>
                  直接設定
                </button>
                <button type="button" onClick={() => { setAdjustmentType('add'); setQuantity(0); }} className={`flex-1 py-2 rounded-lg border transition-colors ${adjustmentType === 'add' ? 'bg-green-600 text-white border-green-600' : 'border-gray-300'}`}>
                  <Plus size={16} className="inline mr-1" />追加
                </button>
                <button type="button" onClick={() => { setAdjustmentType('subtract'); setQuantity(0); }} className={`flex-1 py-2 rounded-lg border transition-colors ${adjustmentType === 'subtract' ? 'bg-red-600 text-white border-red-600' : 'border-gray-300'}`}>
                  <Minus size={16} className="inline mr-1" />減少
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                {adjustmentType === 'set' ? '新しい在庫数' : adjustmentType === 'add' ? '追加数' : '減少数'}
              </label>
              <input
                type="number"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                required
                className="w-full px-4 py-2 border rounded-lg"
                style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>調整理由</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                rows={3}
                placeholder="例: 棚卸し結果、返品受け入れ、破損廃棄など"
                className="w-full px-4 py-2 border rounded-lg resize-none"
                style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary">キャンセル</button>
              <button type="submit" disabled={loading} className="btn-primary">{loading ? '処理中...' : '調整を実行'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// 在庫変動履歴モーダル
function InventoryLogsModal({ onClose }: { onClose: () => void }) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  async function fetchLogs() {
    try {
      const response = await apiFetch('/api/inventory/logs?limit=50');
      const data = await response.json();
      if (response.ok) setLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const reasonLabels: Record<string, string> = {
    order: '受注',
    manual: '手動',
    sync: '同期',
    return: '返品',
    adjustment: '調整',
    transfer: '移動',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>在庫変動履歴</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">×</button>
          </div>

          {loading ? (
            <div className="text-center py-8">読み込み中...</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                  <th className="px-4 py-2 text-left text-xs">日時</th>
                  <th className="px-4 py-2 text-left text-xs">バリアント</th>
                  <th className="px-4 py-2 text-center text-xs">変動</th>
                  <th className="px-4 py-2 text-left text-xs">理由</th>
                  <th className="px-4 py-2 text-right text-xs">変動後在庫</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-4 py-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(log.createdAt).toLocaleString('ja-JP')}
                    </td>
                    <td className="px-4 py-2 text-sm font-mono">{log.variant?.variantCode || log.variantId.substring(0, 8)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`font-bold ${log.changeAmount > 0 ? 'text-green-600' : log.changeAmount < 0 ? 'text-red-600' : ''}`}>
                        {log.changeAmount > 0 ? '+' : ''}{log.changeAmount}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">{reasonLabels[log.reason] || log.reason}</span>
                      {log.reasonDetail && <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>{log.reasonDetail}</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-medium">{log.snapshotStock}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex justify-end mt-6">
            <button onClick={onClose} className="btn-secondary">閉じる</button>
          </div>
        </div>
      </div>
    </div>
  );
}

